/**
 * The Feeds sub-tab of Admin → Directories: where imported data comes FROM.
 *
 * Under Directories rather than beside it, because "where does this data come from" and "what is
 * in it" are the same subject — and this half is touched once per feed and then rarely, so it
 * should not sit as prominently as the records themselves. Not a Field Builder tab either: it has
 * to cover every feed the firm receives, not only the ones that fill in form fields.
 *
 * The page is built around DISCOVERY. An administrator is not expected to know, or to type,
 * the header names in an export: the feed's first delivery records its real header row (see
 * feeds/clientFeed.js), and this page turns those headers into a row per column to map.
 * Mapping a column means saying three things — what to call it, whether it is the record's
 * number or name, and which form field (if any) it should fill in when a record is chosen.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getCountFromServer } from 'firebase/firestore';
import { db } from '../../firebase';
import { CallStatus, useCallable } from '../../lib/useCallable';
import { useDoc } from '../../lib/useFirestore';
import {
  COLUMN_ROLES,
  COLUMN_TRANSFORMS,
  CUSTOM_FEED_ID,
  allFeeds,
  feedRecordsPath,
  suggestColumns,
} from '../../lib/feedRegistry';
import { readClientStatus } from '../../lib/clientStatus';
import { conditionBuilderFieldList } from '../../lib/fieldLayout';
import { FORM_SECTIONS } from '../../lib/formSchema';

const input =
  'rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-2 py-1 text-xs';

/**
 * Importing a whole directory takes longer than the web SDK's 70-second default, and a browser
 * that gives up early reports a failure while the import is still running — indistinguishable,
 * from the outside, from the import not working. Matches the timeoutSeconds on the functions.
 */
const LONG_RUNNING = { timeout: 540000 };

/** Firestore timestamps arrive as Timestamp objects; a hand-written one arrives as a string. */
function millisOf(value) {
  if (!value) return 0;
  const millis = typeof value.toMillis === 'function' ? value.toMillis() : Date.parse(value);
  return Number.isFinite(millis) ? millis : 0;
}

function formatWhen(value) {
  const millis = millisOf(value);
  if (!millis) return value ? 'unknown' : 'never';
  return new Date(millis).toLocaleString();
}

/**
 * The last-run record the poll writes on every invocation, success or not.
 *
 * Also hands its message id back up via `onLoaded`, so the cleanup panel beside it can offer
 * "undo the last import" without the page needing one hook per feed — a hook inside a loop over
 * a list that changes length is exactly the thing that breaks React.
 */
function PollStatus({ feed, onLoaded, settingsChangedAt }) {
  const { data } = useDoc(`system/${feed.pollStatusDoc}`);
  useEffect(() => {
    if (onLoaded) onLoaded(feed.id, data);
  }, [feed.id, data, onLoaded]);
  if (!data) {
    return (
      <p className="text-xs text-slate-500 dark:text-slate-400">
        This feed has not run yet — or has never been able to write its status.
      </p>
    );
  }

  // A run writes its status before AND after the work, so `running` still being set means it
  // started and never reported back — almost always a timeout. Without this the page showed the
  // PREVIOUS run's result as if it were current, which is how an import that died silently came
  // to look like a button that did nothing.
  if (data.running === true) {
    return (
      <p className="text-xs text-amber-800 dark:text-amber-200">
        Started {formatWhen(data.startedAt || data.ranAt)} and has not reported finishing. Either it
        is still going, or it was cut short — run it again and watch this line.
      </p>
    );
  }

  // A result from before the mapping was last changed is not a result about the current mapping.
  const ranAtMs = millisOf(data.ranAt);
  const changedMs = millisOf(settingsChangedAt);
  const stale = ranAtMs && changedMs && changedMs > ranAtMs;

  const outcome = data.outcome || {};
  const summary = outcome.skipped
    ? `skipped (${outcome.skipped})`
    : outcome.imported
      ? `imported${outcome.importResult ? ` — ${JSON.stringify(outcome.importResult).slice(0, 160)}` : ''}`
      : outcome.error
        ? // The detail is a sentence written for a person ("…Exactly one column must be marked as
          // the client number"); the code is for logs. Printing the code is what made "0 records
          // imported" read as a malfunction rather than a step not yet taken.
          outcome.detail || `error: ${outcome.error}`
        : outcome.alreadyProcessed
          ? 'nothing new (every matching email had already been imported)'
          : `found ${outcome.found ?? 0} matching emails`;
  return (
    <div className="text-xs text-slate-600 dark:text-slate-300">
      <p>
        Last run {formatWhen(data.ranAt)} ({data.trigger || 'scheduled'}): {summary}
      </p>
      {stale && (
        <p className="mt-1 rounded bg-amber-50 dark:bg-amber-950 p-2 text-amber-900 dark:text-amber-100">
          <strong>That result is older than your last change to this feed</strong>, so it says
          nothing about the mapping as it stands now. Run it again.
        </p>
      )}
    </div>
  );
}

/**
 * What is actually in the client directory, and how to empty it again.
 *
 * This panel exists so a feed can be TESTED. Loading a made-up file is only safe if the rows
 * can be removed afterwards, and `clients` is writable by no client at all — so removal goes
 * through the adminDeleteClients callable, which is audited.
 *
 * "Undo the last import" is the button that matters: it deletes exactly what that email wrote
 * and releases the claim on it, so the same file can be corrected and re-imported. Deleting
 * everything is deliberately harder to reach than deleting one import's worth.
 */
function ClientDirectoryPanel({ feedId, lastMessageId }) {
  const { run, busy, error, saved } = useCallable();
  const [count, setCount] = useState(null);
  const [countError, setCountError] = useState('');
  const [confirmAll, setConfirmAll] = useState('');
  const [result, setResult] = useState('');
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;
    getCountFromServer(collection(db, ...feedRecordsPath(feedId)))
      .then((snap) => {
        if (!cancelled) setCount(snap.data().count);
      })
      .catch((err) => {
        if (!cancelled) setCountError(err.message || String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [refreshToken, feedId]);

  const remove = async (payload, describe) => {
    setResult('');
    const outcome = await run('adminDeleteClients', { ...payload, feedId });
    // `run` resolves with the callable's data on success and swallows the error into `error`
    // for display, so a missing outcome means it failed and the banner already says so.
    if (outcome) {
      setResult(
        describe(outcome) +
          (outcome.claimReleased ? ' That email can now be imported again.' : '')
      );
      setRefreshToken((token) => token + 1);
    }
  };

  return (
    <div className="mt-3 rounded border border-slate-200 dark:border-slate-700 p-2">
      <p className="text-xs text-slate-600 dark:text-slate-300">
        <strong>
          {countError
            ? 'The client directory could not be counted'
            : count === null
              ? 'Counting the client directory…'
              : `${count.toLocaleString()} record${count === 1 ? '' : 's'} in this directory`}
        </strong>
        {' — '}
        imported records cannot be edited from the app, only removed and re-imported. Every
        removal is written to the firm-wide audit log.
      </p>

      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
        <button
          type="button"
          disabled={busy || !lastMessageId}
          title={
            lastMessageId
              ? 'Deletes exactly the clients that import wrote, and releases the email so it can be imported again'
              : 'No import has run yet'
          }
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-slate-700 dark:text-slate-200 hover:border-firm-500 disabled:opacity-50"
          onClick={() =>
            remove(
              { sourceMessageId: lastMessageId },
              (outcome) => `Removed ${outcome.deleted} record${outcome.deleted === 1 ? '' : 's'} from that import.`
            )
          }
        >
          Undo the last import
        </button>

        {/* Not an import: it recomputes each record's searchable text from what is already
            stored. Needed because records imported before word search existed have no
            searchable text at all, and because changing which columns are searchable changes
            it — neither of which is worth re-importing a whole export for. */}
        <button
          type="button"
          disabled={busy || !count}
          title={
            count
              ? 'Recomputes the searchable text on every record here, without re-importing'
              : 'There are no records to index'
          }
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-slate-700 dark:text-slate-200 hover:border-firm-500 disabled:opacity-50"
          onClick={async () => {
            setResult('');
            const outcome = await run('adminRebuildSearchIndex', { feedId }, LONG_RUNNING);
            if (outcome) {
              setResult(
                `Search index rebuilt: ${outcome.updated.toLocaleString()} of ${outcome.scanned.toLocaleString()} records updated` +
                  (outcome.unchanged ? `, ${outcome.unchanged.toLocaleString()} already current` : '') +
                  '.'
              );
            }
          }}
        >
          Rebuild search index
        </button>

        <span className="text-slate-400 dark:text-slate-500">or</span>

        <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
          Empty the whole directory — type
          <code className="rounded bg-slate-100 dark:bg-slate-800 px-1">DELETE ALL CLIENTS</code>
          <input
            className={`${input} w-44`}
            value={confirmAll}
            placeholder="confirmation"
            onChange={(e) => setConfirmAll(e.target.value)}
          />
        </label>
        <button
          type="button"
          disabled={busy || confirmAll.trim() !== 'DELETE ALL CLIENTS'}
          className="rounded border border-red-300 dark:border-red-700 px-3 py-1 text-red-700 dark:text-red-300 hover:border-red-500 disabled:opacity-40"
          onClick={() =>
            remove({ all: true, confirm: confirmAll.trim() }, (outcome) => {
              setConfirmAll('');
              return `Removed all ${outcome.deleted} records.`;
            })
          }
        >
          Delete every client
        </button>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      {result && <p className="mt-2 text-xs text-slate-700 dark:text-slate-200">{result}</p>}
    </div>
  );
}

/**
 * Load a CSV by hand, to seed a directory or to test a mapping without waiting for a delivery.
 *
 * Runs the SAME import the nightly poll runs — deliberately, because a seed that behaved
 * differently from the real thing would prove the wrong thing — and records itself in the same
 * claim log, so "Undo the last import" removes it exactly like an emailed one.
 *
 * "Read its columns only" is the first thing to press on a brand-new feed: it records the
 * export's real headers so they can be mapped, and stores nothing.
 */
function SeedUploadPanel({ feedId, mapped }) {
  const { run, busy, error, saved } = useCallable();
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState('');
  const [result, setResult] = useState('');

  const readFile = (file) => {
    if (!file) return;
    setFileName(file.name);
    setResult('');
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ''));
    reader.onerror = () => setResult('That file could not be read.');
    reader.readAsText(file);
  };

  const send = async (mode) => {
    setResult('');
    const outcome = await run('adminImportFeedCsv', { feedId, csvText: text, mode }, LONG_RUNNING);
    if (!outcome) return; // the error banner already says why
    if (outcome.mode === 'discover') {
      setResult(
        'Read ' + outcome.rowCount + ' rows and ' + outcome.headers.length +
          ' columns. Nothing was stored — map the columns below, then import.'
      );
    } else if (outcome.ok) {
      setResult('Imported. ' + (outcome.detail || ''));
    } else {
      setResult('Not imported: ' + (outcome.detail || outcome.error));
    }
  };

  const lineCount = text ? text.split(/\r?\n/).filter((line) => line.trim()).length : 0;

  return (
    <div className="mt-3 rounded border border-slate-200 dark:border-slate-700 p-2">
      <p className="text-xs text-slate-600 dark:text-slate-300">
        <strong>Load a file by hand.</strong> Same import as the nightly one, and undoable the same
        way — use it to seed this directory, or to check a mapping against a real export before the
        feed is switched on.
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-3 text-xs">
        <input
          type="file"
          accept=".csv,text/csv,text/plain"
          className="text-xs"
          onChange={(e) => readFile(e.target.files && e.target.files[0])}
        />
        {lineCount > 0 && (
          <span className="text-slate-500 dark:text-slate-400">
            {fileName ? fileName + ' — ' : ''}
            {(lineCount - 1).toLocaleString()} rows ready
          </span>
        )}
        <button
          type="button"
          disabled={busy || !text.trim()}
          className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-slate-700 dark:text-slate-200 hover:border-firm-500 disabled:opacity-50"
          onClick={() => send('discover')}
        >
          Read its columns only
        </button>
        <button
          type="button"
          disabled={busy || !text.trim() || !mapped}
          title={mapped ? 'Imports the file now' : 'Map the number and name columns first'}
          className="rounded bg-firm-700 dark:bg-firm-500 px-3 py-1 font-medium text-white hover:bg-firm-900 disabled:opacity-40"
          onClick={() => send('import')}
        >
          Import this file
        </button>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>
      {result && <p className="mt-2 break-words text-xs text-slate-700 dark:text-slate-200">{result}</p>}
      <details className="mt-2">
        <summary className="cursor-pointer text-xs text-slate-500 dark:text-slate-400">
          or paste the file contents
        </summary>
        <textarea
          className="mt-1 h-24 w-full rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 p-2 font-mono text-xs"
          placeholder="CLIENT_CODE,CLIENT_NAME,…"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setFileName('');
          }}
        />
      </details>
    </div>
  );
}

export default function AdminFeeds() {
  const { data: stored, loading } = useDoc('settings/feeds');
  const { data: discovery } = useDoc('system/feedDiscovery');
  const [newFeedId, setNewFeedId] = useState('');
  const [newFeedLabel, setNewFeedLabel] = useState('');
  const [feedPollStatus, setFeedPollStatus] = useState({});
  const notePollStatus = useCallback(
    (id, data) => setFeedPollStatus((prev) => (prev[id] === data ? prev : { ...prev, [id]: data })),
    []
  );
  const { data: fieldLayout, loading: layoutLoading } = useDoc('settings/fieldLayout');
  const { run, busy, error, saved } = useCallable();
  const [feeds, setFeeds] = useState(null);

  useEffect(() => {
    if (!loading && feeds === null) setFeeds(structuredClone(stored?.feeds || {}));
  }, [loading, stored, feeds]);

  // The same field list the condition builder and the auto-fill rules use, so a column's
  // destination is chosen from the real, effective form rather than typed as a path.
  const targetFields = useMemo(
    () => conditionBuilderFieldList(FORM_SECTIONS, fieldLayout),
    [fieldLayout]
  );

  if (loading || layoutLoading || feeds === null) {
    return <p className="text-sm text-slate-500 dark:text-slate-400">Loading feeds…</p>;
  }

  // Built-ins plus whatever an administrator has added to settings/feeds.
  const feedList = allFeeds(feeds);
  const settingsFor = (id) => feeds[id] || {};
  const patchFeed = (id, patch) =>
    setFeeds((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  const patchColumn = (id, index, patch) =>
    setFeeds((prev) => {
      const columns = [...((prev[id] && prev[id].columns) || [])];
      columns[index] = { ...columns[index], ...patch };
      return { ...prev, [id]: { ...(prev[id] || {}), columns } };
    });

  /**
   * Marks exactly one column as the one that may hold unquoted commas.
   *
   * Exclusive by construction rather than by validation: the repair in feeds/csvRows.js
   * reconstructs a row around ONE such column, so leaving it possible to tick two would be
   * offering a setting that cannot work.
   */
  const setCommaColumn = (id, header) =>
    setFeeds((prev) => {
      const columns = ((prev[id] && prev[id].columns) || []).map((column) => {
        const next = { ...column };
        if (column.header === header) next.mayContainCommas = true;
        else delete next.mayContainCommas;
        return next;
      });
      return { ...prev, [id]: { ...(prev[id] || {}), columns } };
    });

  const save = () => run('adminUpdateSettings', { docId: 'feeds', value: { feeds } });

  /**
   * Turns the headers the feed actually saw into mappable rows, with a suggested role for each.
   *
   * Additive on purpose: a header that has disappeared from the export keeps its row, so a
   * mapping is not silently lost to one truncated delivery. Suggestions apply only to rows being
   * ADDED, never to a row that already exists — a decision already made outranks a guess.
   */
  const adoptDiscoveredColumns = (id) => {
    const headers = (discovery && discovery[id] && discovery[id].headers) || [];
    const existing = (feeds[id] && feeds[id].columns) || [];
    const byHeader = new Map(existing.map((column) => [column.header, column]));
    const taken = existing
      .map((column) => column.role)
      .filter((role) => role && role !== 'data' && role !== 'ignore');
    const merged = [...existing];
    for (const column of suggestColumns(headers.filter((header) => !byHeader.has(header)))) {
      // Never suggest a second number/name/status when the saved mapping already names one.
      const clash = column.role !== 'data' && taken.includes(column.role);
      merged.push(clash ? { ...column, role: 'data' } : column);
      if (!clash && column.role !== 'data') taken.push(column.role);
    }
    patchFeed(id, { columns: merged });
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={busy}
          className="rounded bg-firm-700 dark:bg-firm-500 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:hover:bg-firm-400 disabled:opacity-50"
        >
          Save feeds
        </button>
        <CallStatus busy={busy} error={error} saved={saved} />
      </div>

      <p className="rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3 text-xs text-slate-600 dark:text-slate-300">
        A feed is a CSV a system emails to the intake mailbox each night. The app finds the
        newest matching email, imports it exactly once, and records what happened. Nothing is
        imported until a feed is <strong>enabled</strong>; before the columns are mapped, a run
        records the export&apos;s real headers and imports nothing, so the mapping can be
        finished from the actual file.
      </p>

      {feedList.map((feed) => {
        const settings = settingsFor(feed.id);
        const columns = settings.columns || [];
        const found = (discovery && discovery[feed.id]) || null;
        const numberColumn = columns.find((column) => column.role === 'number');
        const nameColumn = columns.find((column) => column.role === 'name');
        const statusColumn = columns.find((column) => column.role === 'status');
        const commaColumn = columns.find((column) => column.mayContainCommas === true);
        /**
         * Mapped columns whose header is not in the export that actually arrived.
         *
         * Worth saying out loud on this page rather than only in a log. One wrong header on the
         * number or name column skips EVERY row — the mapping said `CLIENT_NAME` while the export
         * said `Client_Name`, and the import reported "all 9,984 rows were skipped" without
         * naming either.
         */
        const missingHeaders =
          found?.headers && columns.length
            ? columns
                .filter((column) => column.header && column.role !== 'ignore')
                .map((column) => column.header)
                .filter((header) => !found.headers.includes(header))
            : [];
        const missingIdentity = columns.filter(
          (column) =>
            (column.role === 'number' || column.role === 'name') && missingHeaders.includes(column.header)
        );
        // What the sample row's own status value will actually be read as. Worth showing
        // rather than describing: "Prospect → active (not recognized)" tells an administrator
        // in one glance whether they need to teach it a value.
        const sampleStatus =
          statusColumn && found?.sampleRow ? String(found.sampleRow[statusColumn.header] ?? '') : '';
        const sampleStatusRead = statusColumn ? readClientStatus(sampleStatus, settings) : null;

        return (
          <section
            key={feed.id}
            className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4"
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">{feed.label}</h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-xs text-slate-700 dark:text-slate-200 hover:border-firm-500 disabled:opacity-50"
                  disabled={busy}
                  onClick={() => run(feed.runCallable, { feedId: feed.id }, LONG_RUNNING)}
                >
                  Run now
                </button>
              </div>
            </div>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{feed.description}</p>
            <div className="mt-2">
              <PollStatus
                feed={feed}
                onLoaded={notePollStatus}
                settingsChangedAt={stored?.updatedAt}
              />
            </div>

            {feed.managedElsewhere ? (
              <p className="mt-3 rounded border border-slate-200 dark:border-slate-700 p-2 text-xs text-slate-500 dark:text-slate-400">
                Configured in code and managed from <strong>{feed.managedElsewhere}</strong> — there is
                nothing to map here.
              </p>
            ) : (
              <>
                <div className="mt-3 flex flex-wrap items-center gap-4 text-xs">
                  <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    <input
                      type="checkbox"
                      checked={settings.enabled === true}
                      onChange={(e) => patchFeed(feed.id, { enabled: e.target.checked })}
                    />
                    Enabled — poll the mailbox daily
                  </label>
                  <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    Email subject contains
                    <input
                      className={`${input} w-48`}
                      value={settings.subjectMatch ?? ''}
                      placeholder={feed.defaultSubjectMatch}
                      onChange={(e) => patchFeed(feed.id, { subjectMatch: e.target.value })}
                    />
                  </label>
                  <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                    Look back
                    <input
                      type="number"
                      min="1"
                      max="30"
                      className={`${input} w-16`}
                      value={settings.lookbackDays ?? 2}
                      onChange={(e) =>
                        patchFeed(feed.id, {
                          lookbackDays: Math.max(1, Math.min(30, Number(e.target.value) || 1)),
                        })
                      }
                    />
                    days
                  </label>
                </div>

                <div className="mt-3 rounded border border-slate-200 dark:border-slate-700 p-2">
                  {found ? (
                    <div className="text-xs text-slate-600 dark:text-slate-300">
                      <p>
                        Last export seen {formatWhen(found.seenAt)}:{' '}
                        <strong>{found.headers?.length || 0}</strong> columns,{' '}
                        {(found.totalRows ?? found.rowCount ?? 0).toLocaleString()} rows.
                      </p>
                      {/* The step people get stuck on. The feed discovers the headings and
                          correctly refuses to guess which is which — but a status line reading
                          "mapping_incomplete" made that look like a failure rather than a
                          decision waiting to be made. */}
                      {columns.length === 0 && (
                        <p className="mt-1 rounded bg-firm-50 dark:bg-firm-900 p-2 text-firm-700 dark:text-firm-100">
                          <strong>Nothing has been imported yet</strong> — the columns have to be
                          mapped first. Press below to list them with a suggested role for each,
                          check those, then <strong>Save feeds</strong> at the top.
                        </p>
                      )}
                      <button
                        type="button"
                        className="mt-2 rounded border border-firm-500 px-3 py-1 font-medium text-firm-700 dark:text-firm-200 hover:bg-firm-50 dark:hover:bg-firm-900"
                        onClick={() => adoptDiscoveredColumns(feed.id)}
                      >
                        {columns.length === 0 ? 'Map these columns' : 'Add any new columns'}
                      </button>
                    </div>
                  ) : (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      No export has been seen yet. Enable the feed and set the subject, then use{' '}
                      <strong>Run now</strong> — the first run records the export&apos;s columns here
                      without importing anything.
                    </p>
                  )}
                  {found?.headers?.length ? (
                    <p className="mt-1 break-words text-xs text-slate-400 dark:text-slate-500">
                      {found.headers.join(' · ')}
                    </p>
                  ) : null}
                </div>

                {/* The two things the importer refuses to guess at. Stated as a live check
                    rather than only as a save-time error, because a half-mapped feed is the
                    normal state while this page is being filled in. */}

                {statusColumn && (
                  <div className="mt-2 rounded border border-slate-200 dark:border-slate-700 p-2">
                    <p className="text-xs text-slate-600 dark:text-slate-300">
                      <strong>{statusColumn.header}</strong> is being read as the active/inactive flag.
                      Active/Inactive, A/I, Y/N, true/false, 1/0 and Open/Closed are understood already —
                      name any others below. <strong>Anything blank or unrecognized counts as active</strong>,
                      so a client is never hidden by a value nobody taught it.
                    </p>
                    <div className="mt-2 flex flex-wrap items-center gap-4 text-xs">
                      <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                        Values meaning inactive
                        <input
                          className={`${input} w-44`}
                          placeholder="e.g. C, Closed, 9"
                          value={settings.inactiveValues ?? ''}
                          onChange={(e) => patchFeed(feed.id, { inactiveValues: e.target.value })}
                        />
                      </label>
                      <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                        Values meaning active
                        <input
                          className={`${input} w-44`}
                          placeholder="e.g. P, Prospect"
                          value={settings.activeValues ?? ''}
                          onChange={(e) => patchFeed(feed.id, { activeValues: e.target.value })}
                        />
                      </label>
                      <label className="flex items-center gap-2 text-slate-600 dark:text-slate-300">
                        <input
                          type="checkbox"
                          checked={settings.includeInactive === true}
                          onChange={(e) => patchFeed(feed.id, { includeInactive: e.target.checked })}
                        />
                        Also offer inactive clients in the picker (marked as inactive)
                      </label>
                    </div>
                    {found?.sampleRow && (
                      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                        In the last export seen, this column held{' '}
                        <strong>{sampleStatus ? `“${sampleStatus}”` : '(blank)'}</strong>, which will be read as{' '}
                        <strong>{sampleStatusRead?.active ? 'active' : 'inactive'}</strong>
                        {sampleStatusRead && !sampleStatusRead.recognized
                          ? ' — that value is not recognized, so it falls back to active. Add it above if it means inactive.'
                          : '.'}
                      </p>
                    )}
                  </div>
                )}
                {missingIdentity.length > 0 && (
                  <p className="mt-2 rounded border border-red-400 dark:border-red-600 bg-red-50 dark:bg-red-950 p-2 text-xs text-red-900 dark:text-red-100">
                    <strong>Nothing will import.</strong> The{' '}
                    {missingIdentity
                      .map((c) => (c.role === 'name' ? 'client name' : 'client number') + ' column “' + c.header + '”')
                      .join(' and ')}{' '}
                    {missingIdentity.length > 1 ? 'are' : 'is'} not in the export that arrived, so every row
                    is skipped for having no {missingIdentity.some((c) => c.role === 'name') ? 'name' : 'number'}.
                    Correct the header above — the export calls its columns{' '}
                    <code>{(found?.headers || []).join(', ')}</code>.
                  </p>
                )}
                {missingHeaders.length > missingIdentity.length && (
                  <p className="mt-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 p-2 text-xs text-amber-900 dark:text-amber-100">
                    Mapped but not in the export that arrived:{' '}
                    <strong>
                      {missingHeaders.filter((h) => !missingIdentity.some((c) => c.header === h)).join(', ')}
                    </strong>
                    . Those columns import nothing. A header shown in red above is one of them.
                  </p>
                )}
                {(!numberColumn || !nameColumn) && (
                  <p className="mt-2 rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950 p-2 text-xs text-amber-900 dark:text-amber-100">
                    Mark one column as the <strong>client number</strong> and one as the{' '}
                    <strong>client name</strong>. Until then this feed records what arrives but
                    imports nothing — a record with no number has no stable identity, and one with
                    no name cannot be found in the picker. A status column is optional — without
                    one, every client counts as active.
                  </p>
                )}

                <SeedUploadPanel feedId={feed.id} mapped={Boolean(numberColumn && nameColumn)} />

                <ClientDirectoryPanel
                  feedId={feed.id}
                  lastMessageId={feedPollStatus[feed.id]?.outcome?.messageId || null}
                />

                {columns.length > 0 && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    <strong>&ldquo;Fills in this form field&rdquo; is the older way to do this.</strong>{' '}
                    The same job is done — and is easier to read, reorder and switch off one at a
                    time — as a <em>Look up a related record</em> rule in{' '}
                    <strong>Admin → Field Builder → Auto-fill rules</strong>, pointed at the
                    client picker on the form. Existing entries here keep working; put new ones
                    over there, so that filling the form in is configured in one place rather
                    than two.
                  </p>
                )}
                {columns.length > 0 && (
                  <div className="mt-3 rounded border border-slate-200 dark:border-slate-700 p-3">
                    <label className="flex flex-wrap items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                      Column that may contain commas
                      <select
                        className={`${input} w-64`}
                        value={commaColumn ? commaColumn.header : ''}
                        onChange={(e) => setCommaColumn(feed.id, e.target.value)}
                      >
                        <option value="">— the client name —</option>
                        {columns
                          .filter((column) => column.header && column.role !== 'ignore')
                          .map((column) => (
                            <option key={column.header} value={column.header}>
                              {column.header}
                            </option>
                          ))}
                      </select>
                    </label>
                    <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                      These exports do not put quotes around a value containing a comma, so a row
                      like <code>JUAN MORALES, INTERIM COMMANDER</code> arrives one field too long
                      and every column after it is a place out. Naming the column the commas are in
                      lets the importer put the row back together. Get it wrong and the affected
                      rows are either refused or filed under the wrong office and practice group —
                      so if an import reports rows it could not realign, this is the setting to
                      check first.
                    </p>
                  </div>
                )}
                {columns.length > 0 && (
                  <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                    Ticked columns are searched as well as the number and name — so a search can
                    find a record by its responsible attorney or office, not just its name. Matching
                    is by whole word or the start of one: &ldquo;smith&rdquo; finds
                    &ldquo;JOHN SMITH HOLDINGS&rdquo;, and &ldquo;mith&rdquo; does not. After changing these ticks press
                    <strong>Rebuild search index</strong> below — the searchable text is derived from the
                    records, so it does not need a fresh import.
                  </p>
                )}
                {columns.length > 0 && (
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full min-w-[980px] text-xs">
                      <thead>
                        <tr className="text-left text-slate-500 dark:text-slate-400">
                          <th className="py-1 pr-2">Column in the export</th>
                          <th className="py-1 pr-2">Store as</th>
                          <th className="py-1 pr-2">Role</th>
                          <th className="py-1 pr-2" title="Include this column when someone searches this directory">
                            Search
                          </th>
                          <th
                            className="py-1 pr-2"
                            title="Fix a quirk of this export before the value is stored"
                          >
                            Clean up
                          </th>
                          <th
                            className="py-1 pr-2"
                            title="The older way to do this — auto-fill rules is the place now"
                          >
                            Fills in this form field
                          </th>
                          <th className="py-1" />
                        </tr>
                      </thead>
                      <tbody>
                        {columns.map((column, index) => (
                          <tr key={`${column.header}-${index}`} className="border-t border-slate-100 dark:border-slate-800">
                            <td className="py-1 pr-2">
                              {/* Editable, because an export renames a column and the mapping then
                                  points at a header that is no longer there. Read-only, the only
                                  way to correct that was to remove the row and re-discover — and
                                  "+ Add a column by hand" produced a row whose header could never
                                  be typed in at all. A mapping cannot be repaired with a control
                                  that does not exist. */}
                              <input
                                className={`${input} w-52 font-medium ${
                                  missingHeaders.includes(column.header)
                                    ? 'border-red-500 dark:border-red-400'
                                    : ''
                                }`}
                                value={column.header || ''}
                                placeholder="Exact header text"
                                onChange={(e) => patchColumn(feed.id, index, { header: e.target.value })}
                                aria-label={`Header for the ${column.key || 'new'} column`}
                              />
                            </td>
                            <td className="py-1 pr-2">
                              <input
                                className={`${input} w-36`}
                                value={column.key || ''}
                                onChange={(e) => patchColumn(feed.id, index, { key: e.target.value })}
                              />
                            </td>
                            <td className="py-1 pr-2">
                              <select
                                className={input}
                                value={column.role || 'data'}
                                onChange={(e) => patchColumn(feed.id, index, { role: e.target.value })}
                              >
                                {COLUMN_ROLES.map(([value, label]) => (
                                  <option key={value} value={value}>
                                    {label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="py-1 pr-2 text-center">
                              {/* The number and name are always searchable — a directory whose
                                  identifier cannot be searched is useless — so the checkbox is
                                  ticked and disabled for them rather than absent, which would
                                  read as "not searchable". */}
                              {column.role === 'number' || column.role === 'name' ? (
                                <input type="checkbox" checked disabled title="Always searchable" />
                              ) : column.role === 'ignore' ? (
                                <span className="text-slate-300 dark:text-slate-600">—</span>
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={column.searchable === true}
                                  onChange={(e) =>
                                    patchColumn(feed.id, index, {
                                      searchable: e.target.checked || undefined,
                                    })
                                  }
                                  aria-label={`Search ${column.header} as well`}
                                />
                              )}
                            </td>
                            <td className="py-1 pr-2">
                              {column.role === 'ignore' ? (
                                <span className="text-slate-300 dark:text-slate-600">—</span>
                              ) : (
                                <select
                                  className={`${input} w-56`}
                                  value={column.transform || ''}
                                  onChange={(e) =>
                                    patchColumn(feed.id, index, {
                                      transform: e.target.value || undefined,
                                    })
                                  }
                                  aria-label={`Clean up ${column.header}`}
                                >
                                  {COLUMN_TRANSFORMS.map(([value, label]) => (
                                    <option key={value} value={value}>
                                      {label}
                                    </option>
                                  ))}
                                </select>
                              )}
                            </td>
                            <td className="py-1 pr-2">
                              <select
                                className={`${input} w-64`}
                                value={column.target || ''}
                                onChange={(e) => patchColumn(feed.id, index, { target: e.target.value })}
                              >
                                <option value="">— nothing, just import it —</option>
                                {targetFields.map((field) => (
                                  <option key={field.path} value={field.path}>
                                    {field.sectionTitle ? `${field.sectionTitle} · ` : ''}
                                    {field.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td className="py-1 text-right">
                              <button
                                type="button"
                                className="text-red-600 dark:text-red-400 hover:underline"
                                onClick={() =>
                                  patchFeed(feed.id, {
                                    columns: columns.filter((_, i) => i !== index),
                                  })
                                }
                              >
                                Remove
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                <button
                  type="button"
                  className="mt-2 rounded border border-dashed border-slate-400 dark:border-slate-500 px-3 py-1 text-xs text-slate-600 dark:text-slate-300 hover:border-firm-500"
                  onClick={() =>
                    patchFeed(feed.id, {
                      columns: [...columns, { header: '', key: '', role: 'data', target: '' }],
                    })
                  }
                >
                  + Add a column by hand
                </button>
              </>
            )}
          </section>
        );
      })}

      <section className="rounded-lg border border-dashed border-slate-300 dark:border-slate-600 p-4">
        <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Add a feed</h2>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
          Any other nightly CSV the firm receives — timekeeper rates, a matter list, an office
          directory. Its records land in <code>directories/&lt;id&gt;/records</code>, which is already
          covered by the security rules and search indexes, so no deploy is needed. Point a
          &ldquo;Client (from a directory)&rdquo; field at it in Field Builder to pick from it on the form.
        </p>
        <div className="mt-2 flex flex-wrap items-end gap-3 text-xs">
          <label className="flex flex-col gap-1 text-slate-600 dark:text-slate-300">
            Id (permanent)
            <input
              className={`${input} w-44`}
              placeholder="timekeeper_rates"
              value={newFeedId}
              onChange={(e) => setNewFeedId(e.target.value.toLowerCase())}
            />
          </label>
          <label className="flex flex-col gap-1 text-slate-600 dark:text-slate-300">
            Name shown here
            <input
              className={`${input} w-56`}
              placeholder="Timekeeper rates"
              value={newFeedLabel}
              onChange={(e) => setNewFeedLabel(e.target.value)}
            />
          </label>
          <button
            type="button"
            disabled={!CUSTOM_FEED_ID.test(newFeedId) || !newFeedLabel.trim() || Boolean(feeds[newFeedId])}
            title={
              feeds[newFeedId]
                ? "A feed with that id already exists"
                : "3-40 characters: lower-case letters, digits and underscores, starting with a letter"
            }
            className="rounded border border-slate-300 dark:border-slate-600 px-3 py-1 text-slate-700 dark:text-slate-200 hover:border-firm-500 disabled:opacity-40"
            onClick={() => {
              patchFeed(newFeedId, {
                label: newFeedLabel.trim(),
                custom: true,
                enabled: false,
                subjectMatch: newFeedId,
                columns: [],
              });
              setNewFeedId('');
              setNewFeedLabel('');
            }}
          >
            Add feed
          </button>
          <span className="text-slate-400 dark:text-slate-500">then Save feeds above</span>
        </div>
      </section>
    </div>
  );
}
